/**
 * ============================================================================
 * [P1-04] PostingModal Component
 *
 * Threads 포스팅 전 최적화 및 확인 모달
 *
 * 기능:
 * - 포털(Portal)을 사용한 모달 렌더링
 * - 원본 vs 최적화 글자 수 비교
 * - 해시태그 자동 추가 옵션
 * - 최종 콘텐츠 미리보기 및 수정
 * - 액션: 복사, Threads 열기, 둘 다 실행
 *
 * Safety:
 * - createPortal 사용 시 document.body 참조
 * - navigator.clipboard 예외 처리
 * ============================================================================
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styles from './PostingModal.module.css'

// ============================================================================
// 타입 정의
// ============================================================================
interface PostingModalProps {
  isOpen: boolean
  onClose: () => void
  content: string
}

// ============================================================================
// [P1-04] PostingModal 컴포넌트
// ============================================================================
export function PostingModal({ isOpen, onClose, content }: PostingModalProps) {
  // ========================================================================
  // 상태 관리
  // ========================================================================
  const [addHashtag, setAddHashtag] = useState(true)
  // content가 undefined일 경우 빈 문자열로 초기화하여 에러 방지
  const [previewContent, setPreviewContent] = useState(content || '')

  // 모달 열릴 때 콘텐츠 동기화
  useEffect(() => {
    if (isOpen) {
      setPreviewContent(content || '')
      setAddHashtag(true) // 기본값: 해시태그 추가
    }
  }, [isOpen, content])

  // ESC 키로 닫기
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [onClose])

  // 포털 컨테이너 확인 (CSR 안전장치)
  if (typeof document === 'undefined') return null
  if (!isOpen) return null

  // ========================================================================
  // 로직: 글자 수 및 최적화
  // ========================================================================
  // 1. 원본 글자 수 (Safe check)
  const safeContent = content || ''
  const originalCount = safeContent.length

  // 2. 최적화된 콘텐츠 (해시태그 제외 가정 등 로직 예시)
  // 실제로는 previewContent를 보여주지만, '최적화된 글자 수'의 정의에 따라 다름
  // 여기서는 단순히 현재 previewContent의 길이를 보여주거나,
  // 해시태그 로직이 적용된 최종 텍스트를 기준으로 함.
  
  // 최종 텍스트 생성 로직
  const safePreviewContent = previewContent || ''
  const finalContent = addHashtag 
    ? `${safePreviewContent.trim()}\n\n#500text` 
    : safePreviewContent.trim()

  const finalCount = finalContent.length

  // ========================================================================
  // 액션 핸들러
  // ========================================================================
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(finalContent)
      alert('클립보드에 복사되었습니다!')
    } catch (err) {
      console.error('Failed to copy:', err)
      alert('복사에 실패했습니다.')
    }
  }

  const handleOpenThreads = () => {
    const text = encodeURIComponent(finalContent)
    // Threads 앱/웹으로 이동
    window.open(`https://www.threads.net/intent/post?text=${text}`, '_blank')
  }

  const handleBoth = async () => {
    await handleCopy()
    handleOpenThreads()
  }

  // ========================================================================
  // 렌더링
  // ========================================================================
  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose} role="dialog" aria-modal="true">
      <div 
        className={styles.modalContent} 
        onClick={(e) => e.stopPropagation()} // 내부 클릭 시 닫힘 방지
      >
        {/* 헤더 */}
        <div className={styles.modalHeader}>
          <h3>📝 Threads 포스팅 최적화 결과</h3>
        </div>

        {/* 통계 (Stats) */}
        <div className={styles.statsContainer}>
          <div className={styles.statRow}>
            <span>원본 글자 수:</span>
            <span className={styles.statValue}>{originalCount}자</span>
          </div>
          <div className={styles.statRow}>
            <span>최적화된 글자 수:</span>
            <span className={styles.statValue}>{finalCount}자</span>
          </div>
        </div>

        {/* 최적화 사항 (Suggestion) */}
        <div className={styles.suggestionBox}>
          <h4>💡 최적화 사항:</h4>
          <ul>
            <li>해시태그 없이 포스팅됩니다. (옵션 선택 시)</li>
            {addHashtag && <li>#500text 해시태그가 자동 추가됩니다.</li>}
          </ul>
        </div>

        {/* 옵션 체크박스 */}
        <div className={styles.optionRow}>
          <label className={styles.checkboxLabel}>
            <input 
              type="checkbox" 
              checked={addHashtag} 
              onChange={(e) => setAddHashtag(e.target.checked)} 
            />
            해시태그 자동 추가
          </label>
        </div>

        {/* 미리보기 (Editable) */}
        <div className={styles.previewContainer}>
          <h4>📋 최종 포스팅 내용 미리보기:</h4>
          <textarea
            className={styles.previewTextarea}
            value={previewContent} // 여기서는 해시태그 미포함 상태로 편집, 최종 전송 시 붙임?
            // 아니면 아예 textarea에 해시태그 붙은 상태로 보여줄지? 
            // UX적으로는 텍스트만 수정하고 해시태그는 옵션으로 붙는게 깔끔함.
            // 하지만 미리보기에는 최종본이 보이는게 좋음.
            // 여기서는 '내용'만 수정하게 하고 최종본은 전송 시 조합.
            onChange={(e) => setPreviewContent(e.target.value)}
          />
          {addHashtag && <div className={styles.hashtagPreview}>+ #500text</div>}
        </div>

        {/* 액션 버튼 */}
        <div className={styles.modalActions}>
           <button onClick={handleCopy} className={`${styles.actionButton} ${styles.blue}`}>
             📋 클립보드 복사
           </button>
           <button onClick={handleOpenThreads} className={`${styles.actionButton} ${styles.orange}`}>
             🚀 Threads 열기
           </button>
           <button onClick={handleBoth} className={`${styles.actionButton} ${styles.green}`}>
             📝🚀 둘 다 실행
           </button>
           <button onClick={onClose} className={`${styles.actionButton} ${styles.gray}`}>
             ❌ 취소
           </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
